<template>
  <div class="user-settings-container">
    <div class="content-area">
      <NavHeader />
      <FloatingCard :with-shadow="false">
        <h5>{{ $t('user_settings.general_settings') }}</h5>
        <div class="column gap-2 top_bottom_padding border-bottom">
          <label class="color-light">{{ $t('user_settings.source') }}</label>
          <FormSelect
            :placeHolder="$t('user_settings.source')"
            :options="availableIndexes"
            v-model="selectedRagIndex"
            :disabled="!availableIndexes.length"
          />
        </div>
        <div class="row top_bottom_padding">
          <label class="color-light">{{ $t('user_settings.language') }}</label>
          <label class="padding-r-2">{{
            $t('user_settings.language.identifier')
          }}</label>
        </div>
      </FloatingCard>
      <!-- Currently disabled -->
      <UserIndexSettings v-if="false" />
    </div>
    <div class="confirmation-section">
      <FormButton
        :label="$t('user_settings.confirm_changes')"
        type="primary"
        :onClick="confirmChanges"
        class-name="w-100"
      />
    </div>

    <CustomModal
      :show="showLeaveModal"
      @update:show="(val: boolean) => (showLeaveModal = val)"
      :onClose="handleLeaveModalClose"
    >
      <div class="modal-content-wrapper">
        <h6>
          {{ $t('user_settings.unsaved_changes_title') }}
        </h6>
        <p>
          {{ $t('user_settings.unsaved_changes_message') }}
        </p>
      </div>
      <template #left-button>
        <FormButton
          :label="$t('user_settings.continue')"
          type="primary"
          :onClick="confirmLeave"
        />
      </template>
      <template #right-button>
        <FormButton
          :label="$t('user_settings.cancel')"
          type="secondary"
          :onClick="cancelLeave"
        />
      </template>
    </CustomModal>
  </div>
</template>

<script setup lang="ts">
import FormButton from '@/components/common/button/FormButton.vue';
import FloatingCard from '@/components/common/floating-card/FloatingCard.vue';
import CustomModal from '@/components/common/modal/CustomModal.vue';
import NavHeader from '@/components/common/nav-header/NavHeader.vue';
import FormSelect from '@/components/common/select/FormSelect.vue';
import UserIndexSettings from '@/components/UserSettings/UserIndexSettings.vue';
import RouteNames from '@/configuration/router/names.routes';
import { IndexIdentifiers, RagIndex } from '@/core/indexesEnum';
import navigationService from '@/services/NavigationService';
import { useAuthStore } from '@/stores/authStore';
import { useUserSettingsStore } from '@/stores/userSettingsStore';
import { useToasterStore } from '@/stores/useToasterStore';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';

const { t } = useI18n();
const settingsStore = useUserSettingsStore();
const toasterStore = useToasterStore();
const { products } = useAuthStore();

const selectedRagIndex = ref(String(settingsStore.selectedRagIndex));
const originalRagIndex = ref(String(settingsStore.selectedRagIndex));
const showLeaveModal = ref(false);
const pendingNavigation = ref<(() => void) | null>(null);

function sortByLabelPriority(arr: { label: string; value: string }[]) {
  const labelOrder = [
    IndexIdentifiers[RagIndex.NAW],
    IndexIdentifiers[RagIndex.ECAY],
    IndexIdentifiers[RagIndex.CissContable],
    IndexIdentifiers[RagIndex.CissFiscal],
    IndexIdentifiers[RagIndex.CissLaboral],
    IndexIdentifiers[RagIndex.LamylineNew],
    IndexIdentifiers[RagIndex.LamylineNotarie],
    IndexIdentifiers[RagIndex.LiaisonsNotarie]
  ];
  return arr.sort(
    (a, b) => labelOrder.indexOf(a.label) - labelOrder.indexOf(b.label)
  );
}

settingsStore.initializeData();

onMounted(() => {
  selectedRagIndex.value =
    settingsStore.selectedRagIndex !== undefined
      ? String(settingsStore.selectedRagIndex)
      : '';
  originalRagIndex.value =
    settingsStore.selectedRagIndex !== undefined
      ? String(settingsStore.selectedRagIndex)
      : '';
});

onBeforeUnmount(() => {
  settingsStore.persist();
});

const hasChanges = computed(() => {
  return selectedRagIndex.value !== originalRagIndex.value;
});

const availableIndexes = computed(() => {
  return sortByLabelPriority(
    products.availableIndexes.map((x) => ({
      value: String(x),
      label: IndexIdentifiers[x]
    }))
  );
});

onBeforeRouteLeave((to, from, next) => {
  if (hasChanges.value) {
    showLeaveModal.value = true;
    pendingNavigation.value = () => next();
    next(false);
  } else {
    next();
  }
});

const confirmChanges = async (): Promise<void> => {
  if (!hasChanges.value) {
    navigationService.navigateTo({ name: RouteNames.Home });
    return;
  }

  const newIndex = selectedRagIndex.value;

  if (newIndex === '') {
    toasterStore.error({
      title: t('user_settings.error_saving_title'),
      body: t('user_settings.error_saving_no_source_message')
    });
    return;
  }

  const ragIndexToSave = Number(newIndex) as RagIndex;
  settingsStore.setSelectedRagIndex(ragIndexToSave);

  originalRagIndex.value = newIndex;

  toasterStore.success({
    title: t('user_settings.changes_saved_title'),
    body: t('user_settings.changes_saved_message')
  });

  navigationService.navigateTo({ name: RouteNames.Home });
};

const confirmLeave = (): void => {
  showLeaveModal.value = false;
  selectedRagIndex.value = originalRagIndex.value;
  navigationService.navigateTo({ name: RouteNames.Home });
};

const cancelLeave = (): void => {
  showLeaveModal.value = false;
  pendingNavigation.value = null;
};

const handleLeaveModalClose = (): void => {
  cancelLeave();
};
</script>

<style scoped lang="scss">
.user-settings-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.content-area {
  flex-grow: 1;
  overflow-y: auto;
}

.confirmation-section {
  padding: $space-3;
  background-color: $color-neutral-50;
}

.row {
  display: flex;
  justify-content: space-between;
  vertical-align: middle;
  min-height: $space-8;
}

.top_bottom_padding {
  padding-top: $space-1;
  padding-bottom: $space-1;
}

.row > label {
  display: flex;
  align-items: center;
}

.row > span {
  display: flex;
  align-items: center;
}

.consumption_cards {
  display: flex;
  flex-direction: row;
  gap: $space-1;
}

.consuption_label {
  display: block;
  text-align: right;
}

.modal-content-wrapper {
  padding: $space-2 0;
}

.modal-content-wrapper h6 {
  margin-bottom: $space-2;
  color: $color-neutral-900;
}

.modal-content-wrapper p {
  margin-bottom: 0;
  color: $color-neutral-700;
}

// Styles for big screens
@media (min-height: $small-screen-height) {
  .top_bottom_padding {
    padding-top: $space-2;
    padding-bottom: $space-2;
  }

  .consumption_cards {
    gap: $space-2;
  }
}
</style>
